import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { holidayLabel } from "../../../../Constant/HR_HolidayTemplate/holidayTemplate";
import { holidayTemplateTitle } from "../../../../Constant/title";
import { deleteHolidayTemplate, listOfHoliday } from "../../../../Api/HolidayTemplate";
import { checkStatusCodeSuccess, errHandler, rendorBackButton } from "../../../../Constant/common";
import { toast } from "react-toastify";
import { notFound } from "../../../../Constant";
import { useSelectedCenter } from "../../../CenterContext";
import BaseModal from "../../../../BaseComponents/BaseModal";
import { DeleteMessage } from "../../../../Constant/Center";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { Plus, Edit2, Users, Trash2, Calendar, AlertCircle } from "react-feather";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";

const HolidayTemplate = () => {
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState([]);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [modal, setModal] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  useEffect(() => {
    document.title = holidayTemplateTitle;
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    listOfHoliday({ condition: { ...selectedCenterPayload } })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) setTemplateData(res?.data);
        else {
          setTemplateData([]);
          toast.error(res?.message);
        }
      })
      .catch(() => setTemplateData([]))
      .finally(() => setLoading(false));
  }, [selectedCenterPayload, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDelete = useCallback((id) => {
    setTemplateId(id);
    setModal((p) => !p);
  }, []);

  const deleteTemplate = async () => {
    setDeleteLoader(true);
    await deleteHolidayTemplate(templateId)
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          toast.success(res?.message);
          fetchData();
        } else toast.error(res?.message);
      })
      .catch(errHandler)
      .finally(() => {
        setDeleteLoader(false);
        toggleDelete();
      });
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {holidayLabel.holidayTemplate}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Manage holiday calendar templates for your organisation
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate("/createHolidayTemplate")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 10,
              background: "var(--brand-indigo)",
              border: "none",
              color: "#fff",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={15} strokeWidth={2.5} /> {holidayLabel.createTemplate}
          </button>
          {rendorBackButton(() => navigate("/setting"))}
        </div>
      </div>

      {/* Template list */}
      {templateData?.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {templateData.map((data, i) => (
            <div
              key={data?.id}
              className="animate-slide-up"
              style={{
                animationDelay: `${i * 40}ms`,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 16,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
                transition: "box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(99,102,241,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  flexShrink: 0,
                  background: "rgba(245,158,11,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Calendar size={20} strokeWidth={1.75} style={{ color: "#f59e0b" }} />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontWeight: 700, fontSize: "0.9375rem", color: "var(--text-primary)" }}
                >
                  {data?.template_name}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: 999,
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      background: "rgba(245,158,11,0.1)",
                      color: "#f59e0b",
                    }}
                  >
                    {data?.holidays_count}{" "}
                    {data?.holidays_count === 1 ? holidayLabel.holiday : holidayLabel.holidays}
                  </span>
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: 999,
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      background: "rgba(16,185,129,0.1)",
                      color: "#10b981",
                    }}
                  >
                    {holidayLabel.staffApplied} {data?.applied_staff_count}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <BaseTooltip label={holidayLabel.editTemplate} color="#b45309">
                  <button
                    onClick={() => navigate(`/editHolidayTemplate/${data?.id}`)}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      border: "1px solid var(--glass-border)",
                      background: "rgba(245,158,11,0.08)",
                      color: "#f59e0b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Edit2 size={14} strokeWidth={2} />
                  </button>
                </BaseTooltip>
                <BaseTooltip label={holidayLabel.manageStaff} color="#047857">
                  <button
                    onClick={() => navigate(`/manageStaff/${data?.id}`)}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      border: "1px solid var(--glass-border)",
                      background: "rgba(16,185,129,0.08)",
                      color: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Users size={14} strokeWidth={2} />
                  </button>
                </BaseTooltip>
                <BaseTooltip label="Delete" color="#be123c">
                  <button
                    onClick={() => toggleDelete(data?.id)}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      border: "1px solid var(--glass-border)",
                      background: "rgba(244,63,94,0.08)",
                      color: "#f43f5e",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </BaseTooltip>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "60px 20px",
            gap: 12,
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
          }}
        >
          <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
          <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
        </div>
      )}

      <BaseModal
        isOpen={modal}
        title="Delete Template"
        toggler={toggleDelete}
        submit={deleteTemplate}
        submitText="Delete"
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        {DeleteMessage}
      </BaseModal>
    </div>
  );
};

export default HolidayTemplate;
